import { CT } from "../lib/tokens";
import { ISO_3166_1, COUNTRY_OTHER } from "../lib/iso3166.mjs";
import { DIVIDER_CODE_PREFIX, isDividerCode as _isDividerCode, isDividerEntry as _isDividerEntry, entryLabelIn } from "../lib/entryLabel.mjs"; // 212 §4
/* BRIEF 124 §1 — the mode vocabulary has ONE owner and it is not this file.
   `src/lib/meetingMode.ts` imports nothing, so there is no cycle; the fold below
   lives here beside `meetingTypeCodes` because it folds taxonomy entries, and
   the VALIDATION lives there because that is where "person" | "online" is
   defined. A fold that validated for itself would be a second vocabulary. */
import { asMeetingMode, type MeetingMode } from "../lib/meetingMode";
// Compat shim: maps backend TaxonomyEntry rows back to the legacy shape each
// frontend consumer currently expects. Lets useTaxonomyList(kind) hand callers
// the same DEFAULT_*-shaped lists they used to consume from utils.js, so the
// render sites didn't have to be rewritten when the source-of-truth moved to
// the TaxonomyEntry table in M+2a.
//
// The bundled fallback values below are the offline/seed copies — the same
// constants that used to live in utils.js as DEFAULT_*. They're consulted
// only when the API errors (toEntriesFromDefault in useTaxonomy.js) and as
// the empty-entries fallback inside toDisplayList.

// 05n — THE CODE IS BACK IN THE LIST, for the six kinds that had lost it.
//
// Every one of these was a flat array of LABELS, because `SHAPES.string`
// discarded the code on the way to the select and the select wrote back what it
// displayed. That is the whole defect: `Case.type` holds "Civil" from an English
// user and "Financier" from a French one, for one entry.
//
// The codes below are NOT new. They are exactly what
// `seed_deploy_taxonomies.js`'s `slug()` has been deriving from these labels
// since the kinds existed, so the rows in every database already carry them —
// which is why re-shaping the canon creates no duplicate entries. The two
// exceptions are named where they occur: `bar`, re-keyed here because the
// slug was mangled and nothing stores one; and `country`, which becomes ISO
// 3166-1 (brief 23 §2, ruled 2026-08-03).
const DEFAULT_CASE_TYPES = [
  ["civil", "Civil"],
  ["commercial", "Commercial"],
  ["criminal", "Criminal"],
  ["labour", "Labour"],
  ["administrative", "Administrative"],
  ["financial", "Financial"],
  ["family", "Family"],
  ["corporate", "Corporate"],
];
// The English labels were the CODES — "pleading", lowercase, in the label
// column — because the canon was a flat list and the seed wrote `label: v`. The
// display site compensated with a hand-rolled title-caser
// (`t.replace(/\b\w/g, c => c.toUpperCase())`, App.tsx), which is brief 22 §2's
// observation: whoever wrote it knew the stored value had to stay raw and
// reached for string cosmetics instead of a code and a label. Now that the two
// are separate the label can be the label. `seed_migration_23_doccat_labels.js`
// repairs the rows that still hold the code as their English label.
const DEFAULT_DOC_TYPES = [
  ["pleading", "Pleading"],
  ["evidence", "Evidence"],
  ["contract", "Contract"],
  ["expert_report", "Expert report"],
  ["correspondence", "Correspondence"],
  ["other", "Other"],
];
const DEFAULT_TITLES = [
  ["mr", "Mr."],
  ["mrs", "Mrs."],
  ["ms", "Ms."],
  ["me", "Me."],
  ["dr", "Dr."],
  ["prof", "Prof."],
];
// 05g — DEFAULT_ROLES is gone with the `collaborator_role` kind it seeded. The
// roles a firm actually has live on the roles plane (/api/roles/labels), which
// is what the collaborator modal has read since 04g. This list never agreed
// with it: it offered `Admin` (not a role), `Of Counsel` and
// `Executive Secretary` (the roles are `OfCounsel`, `ExecutiveSecretary`), and
// could not offer `Paralegal` or `Reception` at all.
// RE-KEYED, and this is the one kind brief 23 §2 asked about answered with a
// measurement. Two of the five codes were mangled — `barreau_de_li_ge` and
// `rak_m_nchen` — because the seed's `slug()` lacks the NFKD normalise
// `slugifyCode()` has (`routes/taxonomies.js`), so it dropped the accented
// letter instead of folding it. The kind has ZERO stored values anywhere
// (measured across every column that feeds it, on all three staging tenants and
// in prod's census), so re-keying it orphans nothing. That is only true while
// no column holds a code, and this slice is what ends that window.
const DEFAULT_BARS = [
  ["balie_brussel", "Balie Brussel"],
  ["balie_antwerpen", "Balie Antwerpen"],
  ["barreau_de_bruxelles", "Barreau de Bruxelles"],
  ["barreau_de_liege", "Barreau de Liège"],
  ["rak_munchen", "RAK München"],
];
// ISO 3166-1 alpha-2 — 249 entries plus `other`. RULED by Laurent, 2026-08-03
// (brief 23 §2): seven hand-picked countries cannot describe a client base
// already in thirteen, and a closed universal vocabulary means no future import
// can produce an unmappable country.
//
// Re-keying `belgium` -> `BE` would normally be the rename this whole slice
// exists to prevent. It is not, for one reason that is true exactly now: NO
// STORED VALUE ANYWHERE HOLDS A CODE. Every column holds a label, so the
// migration converts label -> ISO code in one step and the old codes orphan
// nothing, because nothing ever pointed at them.
//
// The labels are not hand-typed and they are not this file's: they are CLDR's,
// via `scripts/gen_iso_3166.mjs`. Measured against the seven entries this
// replaces, all three languages of all six real countries match CLDR exactly —
// the old list came from the same lineage.
const DEFAULT_COUNTRIES = [
  ...ISO_3166_1.map(([code, en]) => [code, en]),
  [COUNTRY_OTHER[0], COUNTRY_OTHER[1]],
];
const DEFAULT_STAGE_TYPES = [
  ["first_instance", "First Instance"],
  ["appeal", "Appeal"],
  ["cassation", "Cassation"],
  ["opposition", "Opposition"],
  ["third_party_opposition", "Third-party opposition"],
  ["referral_after_cassation", "Referral after cassation"],
];
const DEFAULT_CLIENT_TYPES = [
  ["individual", "Individual"],
  ["company", "Company"],
];
// 182 B.1 — the two CRM kinds, both FREE (nothing branches on a code): where
// a prospect came from, and why one did not become a client. The codes are the
// brief's; a firm edits both lists in Settings like any FREE kind. FR/NL for
// every row are in server/scripts/seed_deploy_taxonomy_translations.js.
const DEFAULT_LEAD_SOURCES = [
  ["referral", "Referral"],
  ["existing_client", "Existing client"],
  ["website", "Website"],
  ["bar", "Bar"],
  ["event", "Event"],
  ["other", "Other"],
];
const DEFAULT_LOST_REASONS = [
  ["no_answer", "No answer"],
  ["price", "Price"],
  ["conflict", "Conflict of interest"],
  ["went_elsewhere", "Went elsewhere"],
  ["other", "Other"],
];
// 189 A.1 — where a client's marketing consent (or refusal) came from. PARTIAL:
// `campaign` is written by the unsubscribe pull-back (189 (3)) and is protected;
// the rest is the firm's list. FR/NL in seed_deploy_taxonomy_translations.js.
const DEFAULT_CONSENT_SOURCES = [
  ["in_person", "Asked in person"],
  ["form", "Form"],
  ["event", "Event"],
  ["import", "Import"],
  ["campaign", "Unsubscribed from a campaign"],
];
// 214 §4 [RULED 53] — a client's contact's role. PARTIAL: `lawyer` is the client's own
// counsel (joint representation) and is bound; a firm adds its own codes in Settings.
// FR/NL in seed_deploy_taxonomy_translations.js.
const DEFAULT_CONTACT_ROLES = [
  ["contact", "Contact"],
  ["lawyer", "Lawyer"],
];
const DEFAULT_CASE_STATUSES = [
  ["active", "Active"],
  // 55 §3 — the CODE stays `pending` (an identity; changing it is a data
  // migration across two tables, a column name, every === comparison, and
  // every persisted filter). Only the WORD changes.
  ["pending", "Suspended"],
  ["closed", "Closed"],
  ["archived", "Archived"],
];
const DEFAULT_TASK_STATUSES = [
  ["todo", "To do"],
  ["inprogress", "In progress"],
  ["waiting", "Waiting"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
  ["archived", "Archived"],
];
/* BRIEF 136 §3 [RULED 2026-08-25, Laurent] — the four event statuses, as the
   SEEDED DEFAULT rather than the only possible wording: a firm renames them in
   Settings -> Taxonomies like every other list. Three of the four are values of
   `AgendaEvent.status`; `archived` is the Phase-28 boolean's word, kept in the
   vocabulary because it is one of the four Laurent named and it is what an
   archived event is described BY. See src/lib/eventStatus.ts. */
const DEFAULT_EVENT_STATUSES = [
  ["confirmed", "Confirmed"],
  ["unconfirmed", "Unconfirmed"],
  ["cancelled", "Cancelled"],
  ["archived", "Archived"],
];
const DEFAULT_REMINDER_OPTS = [
  ["none", "None"],
  ["0", "At due date"],
  ["1", "1 day before"],
  ["2", "2 days before"],
  ["3", "3 days before"],
  ["7", "1 week before"],
  ["14", "2 weeks before"],
  ["30", "1 month before"],
];
// `eisen` — the entry's default Eisenhower quadrant, seeded into
// meta.eisen and read back by eisenAuto() (src/utils.ts). It is DATA, not
// code: the values below are the frozen contents of the former
// EISEN_AUTO_UI / EISEN_AUTO_I constants, which baked legal activity codes
// into a generic view. A code with no `eisen` has no default and lands in
// "s" — that is why 13 of the 24 carry none.
const DEFAULT_TASK_TYPES = [
  { code: "ADMIN", desc: "Administration of the file" },
  { code: "AUD", desc: "Hearing and preparation", eisen: "ui" },
  { code: "CCL", desc: "Drafting of submission", eisen: "ui" },
  { code: "CIT", desc: "Drafting of a summons", eisen: "i" },
  { code: "CONTR", desc: "Drafting of contract", eisen: "i" },
  { code: "CORR", desc: "Drafting of correspondence", eisen: "i" },
  { code: "DEM", desc: "Operations" },
  { code: "EXCORR", desc: "Reading of correspondence", eisen: "i" },
  { code: "EXDOSS", desc: "Study of the evidence" },
  { code: "EXPROC", desc: "Study of procedural documents" },
  { code: "MEMO", desc: "Drafting of a memo", eisen: "i" },
  { code: "OPI", desc: "Drafting of a legal opinion", eisen: "i" },
  { code: "PREP", desc: "Preparation" },
  { code: "RECHDOC", desc: "Documentary research" },
  { code: "RECHJUR", desc: "Legal research" },
  { code: "REQ", desc: "Drafting of a petition", eisen: "i" },
  { code: "REU", desc: "Physical meeting and preparation" },
  { code: "REV", desc: "Revision of a draft", eisen: "i" },
  { code: "TEL", desc: "Telephone call" },
  { code: "TRA", desc: "Translation", eisen: "i" },
  { code: "VIR", desc: "Virtual meeting and preparation" },
  { code: "DOC-R", desc: "Reading of document" },
  { code: "DOC-RV", desc: "Reviewing of document" },
  { code: "DOC-W", desc: "Writing of document" },
];
// `location` — where an event of this type is HELD, as the shape the modal's
// autofill resolves against (meta.location; see resolveEventLocation in
// src/utils.ts). It is DATA, not code: the values below are the frozen
// contents of the former genAgLocation ladder, which named Belgian legal
// event codes inside a generic view. Three modes, all profession-neutral:
//   linkedPlace — the case's linked place (today: its jurisdiction)
//   static      — `value`, rendered verbatim
//   firmRoom    — the firm's default meeting location
// A code with no `location` autofills nothing — that is why 7 of the 13
// carry none.
//
// `firmWide` — P20b. TRUE means every collaborator sees events of this type
// regardless of case scope (scope §4 universal exceptions / D10), on the GET
// and on the SSE hub alike; server/src/lib/eventTypeScope.js is the predicate.
// Frozen content of the former `type === 'REU'` tests: physical meetings are
// firm-visible so meeting rooms can be coordinated. This one is a VISIBILITY
// switch — the only meta key here that widens who can read a row.
//
// `m365ImportRole` — P20c. Which type a meeting coming FROM Microsoft 365
// lands on: 'virtual' for an online meeting, 'inPerson' for a physical one.
// Frozen content of the former IMPORT_ACTIVITY_TYPES = {VIR, REU} set and the
// `isVirtual ? 'VIR' : 'REU'` ladder (server/src/routes/agenda.js). At most one
// type may hold each role — a firm renaming or replacing its meeting types
// retags them here instead of the import knowing two legal codes.
//
// `alertLeadDays` — brief 46 §1. The days-before schedule on which an ALL-DAY
// event of this type alerts. An all-day event has no `time`, and the timed scan
// selects `time: { not: null }` (alertEngine.js:370), so until this key existed
// a prescription deadline was excluded from the alert path BY CONSTRUCTION —
// security-register item 66's substance. The engine now reads this list and
// fires one alert per lead day, at/after 08:00 Brussels.
//
// IT IS A SCHEDULE PER TYPE, NOT A CARVE-OUT FOR `PRE`. Every all-day type can
// carry one; a firm retunes it in Settings with no deploy. That is the P20a/b/c
// seam, taken a fourth time.
//
// ABSENCE IS NOT ZERO. A type with no `alertLeadDays` behaves exactly as it
// does today — no query reaches it, no alert fires. A type nobody has
// configured must never silently start alerting, so the key is omitted
// entirely rather than emitted empty.
const DEFAULT_EVT_TYPES = [
  { code: "INTRO", desc: "Introductive hearing", color: CT.c_a78bfa, location: { mode: "linkedPlace" } },
  { code: "CCL", desc: "Submission", color: CT.c_4a7cff, location: { mode: "static", value: "Online" } },
  /* BRIEF 124 §1 — `defaultMode`. The two rows whose NAMES still encode the mode
     123 moved onto the event now DECLARE it instead, so the words and the
     behaviour agree again. NOT A RENAME: `desc` is tenant data (D28) and
     renaming the canon would diverge every firm that kept the old words — but
     the labels have become describable-as-defaults, so a firm may rename them
     in Settings whenever it likes. */
  { code: "REU", desc: "Meeting in person", color: CT.c_34d399, location: { mode: "firmRoom" }, firmWide: true, m365ImportRole: "inPerson", defaultMode: "person" },
  { code: "VIR", desc: "Virtual meeting", color: CT.c_22d3ee, location: { mode: "static", value: "Online" }, m365ImportRole: "virtual", defaultMode: "online" },
  { code: "PPT", desc: "Skeleton argument", color: CT.c_fbbf24 },
  { code: "AUD", desc: "Hearing", color: CT.c_f472b6, location: { mode: "linkedPlace" } },
  { code: "PRO", desc: "Pronouncement", color: CT.c_f87171, location: { mode: "linkedPlace" } },
  { code: "EXP", desc: "Expert meeting", color: CT.c_fb923c },
  { code: "MED", desc: "Mediation", color: CT.c_a3e635 },
  { code: "DL", desc: "Deadline", color: CT.c_fbbf24 },
  /* RULED by Laurent, 2026-08-07 — sixty, thirty, fourteen, seven, one.
     His reasoning, recorded because the numbers are meaningless without it: the
     work a limitation period triggers — a writ, an expert, foreign counsel —
     takes WEEKS to prepare, so the first warning must land while it is still
     ACTIONABLE rather than merely true. A schedule that starts a fortnight out
     tells a lawyer something they can no longer do much about. */
  { code: "PRE", desc: "Prescription deadline", color: CT.c_ef4444, alertLeadDays: [60, 30, 14, 7, 1] },
  { code: "TASK", desc: "Task", color: CT.c_94a3b8 },
  { code: "OTHER", desc: "Other", color: CT.c_8892a4 },
];
// PROVISIONING CANON — seeded, never substituted on screen. See
// SEED_ONLY_KINDS below for why this one list is treated differently from
// every other DEFAULT_* here.
const DEFAULT_ITEM_LEVEL_LIST = [
  { v: "low", l: "L", c: CT.c_34d399 },
  { v: "medium", l: "M", c: CT.c_fbbf24 },
  { v: "high", l: "H", c: CT.c_f87171 },
];
const DEFAULT_DOC_STATUSES = [
  { v: "draft1", l: "First draft", c: CT.c_8892a4 },
  { v: "draft_final", l: "Final draft", c: CT.c_4a7cff },
  { v: "draft_approved", l: "Client approved", c: CT.c_f59e0b },
  { v: "filed", l: "Filed in court", c: CT.c_34d399 },
  { v: "sent", l: "Sent to opponent", c: CT.c_34d399 },
  { v: "received", l: "Received", c: CT.c_8892a4 },
  { v: "archived", l: "Archived", c: CT.c_8892a4 },
];
const DEFAULT_LINK_TYPES = [
  {
    id: "sharepoint",
    name: "SharePoint",
    icon: "HardDrive",
    iconColor: CT.c_4a7cff,
    nameColor: CT.c_4a7cff,
  },
  {
    id: "onedrive",
    name: "OneDrive",
    icon: "Cloud",
    iconColor: CT.c_4a7cff,
    nameColor: CT.c_4a7cff,
  },
  {
    id: "dropbox",
    name: "Dropbox",
    icon: "Folder",
    iconColor: CT.c_4a7cff,
    nameColor: CT.c_4a7cff,
  },
  {
    id: "gdrive",
    name: "Google Drive",
    icon: "Folder",
    iconColor: CT.c_34d399,
    nameColor: CT.c_34d399,
  },
  {
    id: "website",
    name: "Website",
    icon: "Globe",
    iconColor: CT.c_a78bfa,
    nameColor: CT.c_a78bfa,
  },
  {
    id: "document",
    name: "Document",
    icon: "FileText",
    iconColor: CT.c_fbbf24,
    nameColor: CT.c_fbbf24,
  },
  {
    id: "other",
    name: "Other",
    icon: "Link",
    iconColor: CT.c_8892a4,
    nameColor: CT.c_8892a4,
  },
];
// M+3.6 C4 — three new kinds replacing hardcoded constants in C5.
// 180 §6 — ["draft","Draft"] and ["ready","Ready"] STOOD HERE. No path reaches
// either status (179 C); the InvoiceStatus enum keeps them (a type rebuild).
// This list is the fallback/seed for the invoice_payment_status taxonomy —
// rows a firm already holds in its taxonomy table are DATA and are not
// touched by this edit (Settings → Taxonomies deletes them, by hand).
const DEFAULT_INVOICE_PAYMENT_STATUSES = [
  ["unpaid", "Unpaid"],
  ["partially_paid", "Partial"],
  ["paid", "Paid"],
  ["cancelled", "Cancelled"],
];
const DEFAULT_WORK_LOCATIONS = [
  ["onsite", "Onsite"],
  ["thirdparty", "Third Party"],
  ["home", "Home"],
];
// Phase M+3.7 — Eisenhower quadrants moved into the taxonomy table for
// multilingual labels. The frontend EISEN_LABELS constant stays as the
// English fallback for the < 100ms before the taxonomy hook hydrates.
const DEFAULT_EISENHOWER = [
  ["ui", "Urgent & Important"],
  ["u", "Urgent"],
  ["i", "Important"],
  ["s", "Spare time"],
];
// Phase 14 — external_link kind. No bundled defaults: external links are
// firm-specific and entirely admin-managed, so the offline fallback is an
// empty list (the External page shows its empty state during an outage).
const DEFAULT_EXTERNAL_LINKS: any[] = [];
// 189 §B (167 R7) — custom-field DEFINITIONS. No bundled defaults: a firm adds
// its own fields to clients, cases and collective cases without code, and the
// meta (type · entities · list · showInList) is normalised in
// src/lib/crmDefinitions.mjs, the one home both ends read.
const DEFAULT_CUSTOM_FIELDS: unknown[] = []; // typed unknown, not any: the ratchet counts every any
// 189 §C — STAGE SETS: named, ordered lists of `case_stage` codes a firm defines
// once and applies to a case, so a new case starts with the right stages. No
// bundled defaults; the meta shape is src/lib/crmDefinitions.mjs's.
const DEFAULT_STAGE_SETS: unknown[] = [];
// 189 §D — PLAYBOOKS: when X happens (one of TWO coded triggers), create this
// set of tasks. No bundled defaults; the meta shape is crmDefinitions.mjs's.
const DEFAULT_PLAYBOOKS: unknown[] = [];
// 189 §H — MAILING SEGMENTS: a saved query over the firm's clients (type,
// language, archived, prospects, custom fields), re-resolved at fire time. No
// bundled defaults; the filter's shape is crmDefinitions.mjs's.
const DEFAULT_MAIL_SEGMENTS: unknown[] = [];
// 190 §C — BENTO TEMPLATES: named column sets a Bento offers (a template says
// which of the three surfaces may offer it — `user` and `case` are over
// tasks, `list` over cases). No bundled defaults; the meta shape is
// crmDefinitions.mjs's (normalizeBentoTemplateMeta).
const DEFAULT_BENTO_TEMPLATES: unknown[] = [];
// 110 §2 — party_role's bundled default: the 8 canon codes
// seed_deploy_party_roles.js plants, multilingual labels included, ENTRY-
// shaped (this kind has no display-list shape on purpose — its consumers
// read entries; useTaxonomy's fallback wrapper passes these through). Before
// this, party_role was the kind with NO default, so one failed fetch cached
// an empty list and every role picker said "No match" for the session.
const DEFAULT_PARTY_ROLES = [
  { code: "demandeur", label: "Plaintiff", labelEn: "Plaintiff", labelFr: "Demandeur", labelNl: "Eiser" },
  { code: "defendeur", label: "Defendant", labelEn: "Defendant", labelFr: "Défendeur", labelNl: "Verweerder" },
  { code: "appelant", label: "Appellant", labelEn: "Appellant", labelFr: "Appelant", labelNl: "Appellant" },
  { code: "intime", label: "Respondent on appeal", labelEn: "Respondent on appeal", labelFr: "Intimé", labelNl: "Geïntimeerde" },
  { code: "intervenant_volontaire", label: "Voluntary intervener", labelEn: "Voluntary intervener", labelFr: "Intervenant volontaire", labelNl: "Vrijwillige tussenkomst" },
  { code: "intervenant_force", label: "Forced intervener", labelEn: "Forced intervener", labelFr: "Intervenant forcé", labelNl: "Gedwongen tussenkomst" },
  { code: "conseil_demandeur", label: "Counsel for plaintiff", labelEn: "Counsel for plaintiff", labelFr: "Conseil du demandeur", labelNl: "Raadsman eiser" },
  { code: "conseil_defendeur", label: "Counsel for defendant", labelEn: "Counsel for defendant", labelFr: "Conseil du défendeur", labelNl: "Raadsman verweerder" },
];

// 161 §9 — the overheads sheet's category axis (R9), ruled with these twenty
// rows and this order. Each DB row carries `meta.ledgerAccount` (nullable,
// unused today) — the parked bookkeeping-provider mapping's home; the seed
// (seed_deploy_overhead_categories.js) writes it. Object-shaped like
// DEFAULT_PARTY_ROLES: no display-list shape, consumers read entries.
const DEFAULT_OVERHEAD_CATEGORIES = [
  { code: "management_fees", label: "Management fees", labelEn: "Management fees", labelFr: "Honoraires de gestion", labelNl: "Beheersvergoedingen" },
  { code: "salaries", label: "Salaries", labelEn: "Salaries", labelFr: "Salaires", labelNl: "Lonen" },
  { code: "social_secretariat", label: "Social secretariat", labelEn: "Social secretariat", labelFr: "Secrétariat social", labelNl: "Sociaal secretariaat" },
  { code: "premises_rent", label: "Premises rent", labelEn: "Premises rent", labelFr: "Loyer des locaux", labelNl: "Huur kantoren" },
  { code: "premises_taxes", label: "Premises taxes", labelEn: "Premises taxes", labelFr: "Taxes des locaux", labelNl: "Belastingen kantoren" },
  { code: "premises_insurance", label: "Premises insurance", labelEn: "Premises insurance", labelFr: "Assurance des locaux", labelNl: "Verzekering kantoren" },
  { code: "water", label: "Water", labelEn: "Water", labelFr: "Eau", labelNl: "Water" },
  { code: "electricity", label: "Electricity", labelEn: "Electricity", labelFr: "Électricité", labelNl: "Elektriciteit" },
  { code: "heating", label: "Heating", labelEn: "Heating", labelFr: "Chauffage", labelNl: "Verwarming" },
  { code: "housekeeping", label: "Housekeeping", labelEn: "Housekeeping", labelFr: "Entretien", labelNl: "Onderhoud" },
  { code: "water_and_coffee", label: "Water and coffee", labelEn: "Water and coffee", labelFr: "Eau et café", labelNl: "Water en koffie" },
  { code: "equipment", label: "Equipment", labelEn: "Equipment", labelFr: "Équipement", labelNl: "Uitrusting" },
  { code: "documentation", label: "Documentation", labelEn: "Documentation", labelFr: "Documentation", labelNl: "Documentatie" },
  { code: "professional_insurances", label: "Professional insurances", labelEn: "Professional insurances", labelFr: "Assurances professionnelles", labelNl: "Beroepsverzekeringen" },
  { code: "transportation", label: "Transportation", labelEn: "Transportation", labelFr: "Transports", labelNl: "Vervoer" },
  { code: "car_fleet", label: "Car fleet", labelEn: "Car fleet", labelFr: "Flotte automobile", labelNl: "Wagenpark" },
  { code: "car_insurances", label: "Car insurances", labelEn: "Car insurances", labelFr: "Assurances véhicules", labelNl: "Autoverzekeringen" },
  { code: "communication", label: "Communication", labelEn: "Communication", labelFr: "Communication", labelNl: "Communicatie" },
  { code: "bookkeeping", label: "Bookkeeping", labelEn: "Bookkeeping", labelFr: "Comptabilité", labelNl: "Boekhouding" },
  { code: "other_services", label: "Other services", labelEn: "Other services", labelFr: "Autres services", labelNl: "Andere diensten" },
];

// Authoritative kind → DEFAULT_* constant mapping. Used by useTaxonomy's
// fallback and by toDisplayList's empty-entries path.
export const KIND_TO_DEFAULT: Record<string, any> = {
  case_type: DEFAULT_CASE_TYPES,
  document_category: DEFAULT_DOC_TYPES,
  person_title: DEFAULT_TITLES,
  bar: DEFAULT_BARS,
  country: DEFAULT_COUNTRIES,
  case_stage: DEFAULT_STAGE_TYPES,
  client_type: DEFAULT_CLIENT_TYPES,
  lead_source: DEFAULT_LEAD_SOURCES, // 182 B.1
  lost_reason: DEFAULT_LOST_REASONS, // 182 B.1
  consent_source: DEFAULT_CONSENT_SOURCES, // 189 A.1
  contact_role: DEFAULT_CONTACT_ROLES, // 214 §4
  custom_field: DEFAULT_CUSTOM_FIELDS, // 189 §B — definitions, firm-authored
  stage_set: DEFAULT_STAGE_SETS, // 189 §C — firm-authored
  playbook: DEFAULT_PLAYBOOKS, // 189 §D — firm-authored
  mail_segment: DEFAULT_MAIL_SEGMENTS, // 189 §H — firm-authored
  bento_template: DEFAULT_BENTO_TEMPLATES, // 190 §C — firm-authored
  case_status: DEFAULT_CASE_STATUSES,
  task_status: DEFAULT_TASK_STATUSES,
  event_status: DEFAULT_EVENT_STATUSES, // 136 §3
  reminder_option: DEFAULT_REMINDER_OPTS,
  activity_code: DEFAULT_TASK_TYPES,
  event_type: DEFAULT_EVT_TYPES,
  item_level: DEFAULT_ITEM_LEVEL_LIST,
  document_status: DEFAULT_DOC_STATUSES,
  link_type: DEFAULT_LINK_TYPES,
  invoice_payment_status: DEFAULT_INVOICE_PAYMENT_STATUSES,
  work_location: DEFAULT_WORK_LOCATIONS,
  eisenhower_quadrant: DEFAULT_EISENHOWER,
  external_link: DEFAULT_EXTERNAL_LINKS,
  party_role: DEFAULT_PARTY_ROLES, // 110 §2
  overhead_category: DEFAULT_OVERHEAD_CATEGORIES, // 161 §9
};

// Shape families. M+3.6 C4 adds the three new kinds to the `tuple` family;
// they all serialise to [code, label] pairs with optional meta {color, ...}.
//
// 05n — THE `string` FAMILY IS GONE, and its six kinds are in `tuple`. It was
// the only shape in this switch that dropped the code:
//
//     case "string":  return entries.map((e) => pickEntryLabel(e));
//
// so the option value and the option text were one string, in the language of
// whoever was looking, and the column stored that. `tuple` emits
// `[e.code, label]`, `vlc` emits `{v: e.code, …}`, `activity`/`event`/`link`/
// `external` all emit `code` or `id` — five shapes already did the right thing
// and this one was the outlier (brief 22 §1). The fix was not an invention;
// it was deleting the exception.
//
// The family is deliberately NOT left behind as an empty array. An empty set is
// a check that passes because it has nothing to check — the shape this tree has
// been bitten by four times — and `scripts/lint/taxonomy-classification.js`
// cross-checks LABEL_AS_VALUE against exactly this list. Removing the key makes
// that comparison fail loudly if anyone reintroduces the shape; leaving `[]`
// would make it succeed forever.
const SHAPES = {
  tuple: [
    // 05n — the six former `string` kinds. They were flat label lists; the
    // conversion is in seed_migration_23_*.js and the codes are the ones
    // `slug()` had already been deriving.
    "case_type",
    "document_category",
    "person_title",
    "bar",
    "country",
    "case_stage",
    "client_type",
    "lead_source", // 182 B.1
    "lost_reason", // 182 B.1
    "consent_source", // 189 A.1
    "contact_role", // 214 §4
    "case_status",
    "task_status",
    "event_status", // 136 §3
    "reminder_option",
    "invoice_payment_status",
    "work_location",
    "eisenhower_quadrant",
  ],
  activity: ["activity_code"],
  event: ["event_type"],
  vlc: ["item_level", "document_status"],
  link: ["link_type"],
  // Phase 14 — external_link: code + label + meta {icon, url}.
  external: ["external_link"],
};

function shapeOf(kind: string) {
  for (const [shape, kinds] of Object.entries(SHAPES)) {
    if (kinds.includes(kind)) return shape;
  }
  return null;
}

// Phase M+3.7 — pick the active-language label from a TaxonomyEntry row.
// Falls back labelXX → labelEn → label (legacy single-lang) → code.
// Reads window.__lxLang (set by I18nProvider) so non-React call sites work.
// 206E §4.3 — WIDENED (rule 4) into `pickEntryLabelIn`, which takes a language
// (a pro forma's words are the client's). `pickEntryLabel` stays a
// ONE-argument function over it, because it travels as a value — the 05n
// recording maps it point-free (`entries.map(pickEntryLabel)`), and a second
// parameter caught the array index there (206E's full gate, measured). Its
// result stays exactly the type it always was (`pickEntryLabelIn`'s), so no
// caller's types move — a narrower one rippled through `String()` wrappers
// when `pname` was typed.
export function pickEntryLabel(e: unknown): ReturnType<typeof pickEntryLabelIn> {
  return pickEntryLabelIn(e);
}
// 212 §4 — THE RULE LIVES IN src/lib/entryLabel.mjs (`entryLabelIn`), moved
// there unchanged so the server's MCP reads labels by it too; this function
// keeps what is the browser's: the default language, `window.__lxLang`.
// 110 §3a(ii) — THE DISPLAY SEAM: a divider's label is its heading or
// NOTHING. The code fallback below is right for every ordinary entry (an
// unlabelled entry is at least addressable by its code) and is exactly the
// leak for dividers — `__div__…` reached screens wherever a heading was
// left empty, because this chain ended at `e.code`. Every shape branch of
// toDisplayList and every raw-entry consumer resolves labels through here
// (resolveLongLabel cascades here too), so this one guard is what makes
// "no consumer can ever print a __div__ code" true of the label half;
// the option-site branches (§3a-i) are the value half.
export function pickEntryLabelIn(e: any, lang?: string) {
  return entryLabelIn(e, lang ?? ((typeof window !== "undefined" && window.__lxLang) || "en"));
}

/**
 * Convert TaxonomyEntry[] back to the legacy DEFAULT_* shape for a given kind.
 * If entries is empty/missing, returns the bundled fallback so the app stays
 * functional during first render (before the hook hydrates) and during a
 * fallback window.
 */
// PROVISIONING CANON, NOT A RENDER FALLBACK. Every other kind's DEFAULT_*
// list does double duty — it seeds a new tenant AND stands in on screen when
// the fetch has not landed or has failed. For the level ladder that second
// job IS the defect §0 diagnosed: substituting low/medium/high for a firm
// whose ladder is dominus litis / associate / administrative renders a
// confident answer from a ladder this firm never defined. The codes still
// seed — a tenant with no ladder at all has an unusable app, and §6 keeps the
// codes for exactly that reason — but nothing substitutes them at render
// time. Both render paths consult this set: the empty-entries branch below,
// and useTaxonomy's API-error branch.
// 132 §4 — `legal_holiday` joins it for the same reason and a sharper one:
// substituting a bundled list during an API outage would be INVENTING LAW.
// A kind here falls back to NOTHING, which is what makes the resolver's
// "I do not cover this year" reachable instead of a silent "no holidays".
export const SEED_ONLY_KINDS = new Set(["item_level", "legal_holiday"]);

// BRIEF 108 §6b — THE DIVIDER PREDICATE, ONCE. A divider is an ordinary
// TaxonomyEntry whose meta carries { divider: true }; its CODE mirrors that
// with a reserved prefix the server guarantees at creation (and refuses on
// ordinary entries), so the marker survives every display shape below — the
// code rides through as code/id/v — and every consumer, native select or
// custom picker, can ask one question. Dividers flow THROUGH toDisplayList
// deliberately: the taught renderers (FieldSelect, RolePicker, the raw
// select sites) draw them as separators; a divider can never be a stored
// value, so value-matching consumers never see one.
// 212 §4 — the prefix and both predicates live in src/lib/entryLabel.mjs,
// moved there unchanged; these keep their names and their types here.
export { DIVIDER_CODE_PREFIX };
export function isDividerCode(code: unknown): boolean {
  return _isDividerCode(code);
}
// 110 §3a — meta widened to accept TaxEntry's `Record<string, unknown>` meta
// shape structurally; the read narrows to the one key this predicate asks.
type Dividerish =
  | { code?: unknown; meta?: { divider?: unknown } | Record<string, unknown> | null }
  | null
  | undefined;
export function isDividerEntry(e: Dividerish): boolean {
  return _isDividerEntry(e);
}

// 200 §E — the flag read off one row, typed at the seam (a row arrives `any`).
function secretarialOf(e: unknown): boolean {
  const meta = e && typeof e === "object" ? (e as { meta?: unknown }).meta : null;
  return !!meta && typeof meta === "object" && (meta as { secretarial?: unknown }).secretarial === true;
}

// 207B §6 — WIDENED (rule 4): `lang` labels a tuple kind in another language than
// the reader's (the client form's country, in the client's language); absent,
// exactly what it was — `pickEntryLabel(e)` IS `pickEntryLabelIn(e)`.
export function toDisplayList(entries: readonly any[] | null | undefined, kind: string, lang?: string) {
  if (!entries || entries.length === 0)
    return SEED_ONLY_KINDS.has(kind) ? [] : KIND_TO_DEFAULT[kind] || [];
  const shape = shapeOf(kind);
  switch (shape) {
    case "tuple":
      return entries.map((e) => [e.code, pickEntryLabelIn(e, lang)]);
    case "activity":
      /* 200 §E [RULED — Laurent, 2026-09-20: "for other verticals it should
         not be hard coded"] — `secretarial`: the row's own flag, set in the
         taxonomy editor (meta.secretarial), read by the time-entry modal in
         place of App's former SEC_TASKS literal. A boolean on every row, never
         undefined, so a reader may filter on it without a guard. */
      return entries.map((e) => ({
        code: e.code,
        desc: pickEntryLabel(e),
        secretarial: secretarialOf(e),
      }));
    case "event":
      return entries.map((e) => ({
        code: e.code,
        desc: pickEntryLabel(e),
        color: (e.meta && e.meta.color) || CT.c_8892a4,
      }));
    case "vlc":
      return entries.map((e) => ({
        v: e.code,
        l: pickEntryLabel(e),
        c: (e.meta && e.meta.color) || "var(--tm)",
      }));
    case "link":
      return entries.map((e) => ({
        id: e.code,
        name: pickEntryLabel(e),
        icon: (e.meta && e.meta.icon) || "Link",
        iconColor: (e.meta && e.meta.iconColor) || CT.c_8892a4,
        nameColor: (e.meta && e.meta.nameColor) || CT.c_8892a4,
      }));
    case "external":
      return entries.map((e) => ({
        id: e.code,
        name: pickEntryLabel(e),
        icon: (e.meta && e.meta.icon) || "ExternalLink",
        url: (e.meta && e.meta.url) || "",
      }));
    default:
      return entries;
  }
}
// Attach shapeOf so the hook can call toDisplayList.shapeOf(kind) from its
// fallback path without introducing a second import cycle.
(toDisplayList as any).shapeOf = shapeOf;

// ── activity_code: the default-Eisenhower-quadrant field ────────────────────
// One derivation, three consumers, so the mapping is never hand-copied:
//   · server/scripts/seed_deploy_taxonomies.js — what the seed WRITES to meta
//   · useTaxonomy's toEntriesFromDefault      — the offline/outage fallback
//   · recordings/eisen-default                — the byte-identity guard
/** meta payload the seed writes for one DEFAULT_TASK_TYPES row: the default
 *  Eisenhower quadrant, or null when the code carries none (→ "s"). */
export function activityCodeMeta(x: any) {
  return x && x.eisen ? { eisen: x.eisen } : null;
}

/** Fold loaded activity_code entries into the { code: quadrant } lookup that
 *  eisenAuto() reads (App.tsx publishes it as window.__lxActEisen). Codes with
 *  no meta.eisen are absent from the map — eisenAuto defaults them to "s". */
export function eisenDefaultsFromEntries(entries: any[] | null | undefined) {
  const map: Record<string, string> = {};
  for (const e of entries || []) {
    const q = e && e.meta && e.meta.eisen;
    if (q) map[e.code] = q;
  }
  return map;
}

// ── task_status: the terminal flag ──────────────────────────────────────────
// 60 §4c. THE CANON DID NOT DECLARE WHAT THE MIGRATION WRITES, and the two
// disagreed on every deploy of every box, forever: `seed_deploy_taxonomies.js`
// emitted `meta: null` for this kind while
// `server/scripts/seed_migration_57_task_status_terminal.js` writes
// `{ terminal }`, so D28's projection compared `{}` against `null` and reported
// six divergences that were not firm edits. The mechanism to fix it was already
// in the seed's own header — *"meta IS CO-OWNED, which is why it is compared
// THROUGH a projection"* — and what the projection needs is a canon that
// declares the value.
//
// AND THE REPORT IS THE SMALLER HALF. A tenant provisioned before this declared
// nothing at all, because the canon did not carry it and migration 57 does not
// run at provisioning; those rows fell back to BUILTIN_TERMINAL_STATUSES below,
// which is brief 57's own point — the built-in set is the fallback for a row
// that DECLARES NOTHING. Declaring it here is what makes a new tenant's six rows
// say it themselves.
//
// ONE DERIVATION, and `src/lib/taskCounters.ts` now reads its fallback set from
// this map rather than keeping a second copy of the same six-code fact.
export const TASK_STATUS_TERMINAL: Record<string, boolean> = {
  todo: false,
  inprogress: false,
  waiting: false,
  completed: true,
  cancelled: true,
  archived: true,
};

/** meta payload the seed writes for one DEFAULT_TASK_STATUSES row. A code
 *  outside the six built-ins — a firm's own seventh status — is not this map's
 *  business and gets no meta at all, so it keeps falling back to open.
 *
 *  THE PLANE COLLISION, said out loud: `archived` is a code of `task_status`
 *  AND of `case_status`, and both take the tuple shape. This function answers
 *  by CODE and cannot tell them apart, so both call sites gate on the kind —
 *  `seed_deploy_taxonomies.js`'s own `case 'task_status'` and
 *  `useTaxonomy.ts`'s `kind === "task_status"`. Calling it for another kind
 *  would plant `terminal` on a case status. */
export function taskStatusMeta(
  row: readonly [string, string],
): { terminal: boolean } | null {
  const code = row && row[0];
  return typeof TASK_STATUS_TERMINAL[code] === "boolean"
    ? { terminal: TASK_STATUS_TERMINAL[code] }
    : null;
}

// ── event_status: the icon that REPLACES the counter ────────────────────────
//
// BRIEF 136 §3 — `task_status`'s shape, one entity over, because §3 names it
// the nearest neighbour: a tuple kind whose built-in codes DECLARE a meta the
// canon owns and the seed writes, with the same three consumers kept in step —
//   · server/scripts/seed_deploy_taxonomies.js  what the seed WRITES to meta
//   · useTaxonomy's toEntriesFromDefault        the offline/outage fallback
//   · src/lib/eventStatus.ts                    what reads it while rendering
//
// WHAT THE FLAG IS, AND WHY IT IS THE ICON. `terminal` exists because code
// branches on terminality and a firm's seventh status must be able to declare
// its own answer. The question code asks HERE is "does this status replace the
// day counter, and with what" — which is per-status in exactly the same way.
//
// ABSENT MEANS "DRAW THE COUNTER", which is what makes this safe to leave
// blank: the empty state is the identity behaviour — `confirmed` itself
// declares no icon — so a firm that adds a fifth event status, or clears one
// of these cells, gets the ordinary countdown rather than a meaningless
// symbol.
//   (136 §3 drew the contrast with `chat_message_type`, whose rows had empty
//   icon cells that resolved through `iconByName`'s fallback to MessageCircle
//   for everything. 137 §5 retired that kind, so the counter-example is gone
//   and the reasoning above stands on its own.)
//
// THE TWO NAMES ARE LUCIDE'S OWN, checked against the version this tree pins
// (lucide-react 1.7.0): `CircleQuestionMark` and `CircleOff` both exist and are
// registered in src/lib/lucideRegistry.ts. `archived` declares nothing because
// it is not a value of the column at all — see schema.prisma's AgendaEvent
// `status` and src/lib/eventStatus.ts.
export const EVENT_STATUS_ICON: Record<string, string | null> = {
  confirmed: null,
  unconfirmed: "CircleQuestionMark",
  cancelled: "CircleOff",
  archived: null,
};

/** meta payload the seed writes for one DEFAULT_EVENT_STATUSES row. A code
 *  outside the four built-ins is not this map's business and gets no meta, so
 *  it draws the counter. Gated on the KIND at both call sites, like
 *  `taskStatusMeta` and for the same reason: `cancelled` and `archived` are
 *  codes of `task_status` too. */
export function eventStatusMeta(
  row: readonly [string, string],
): { icon: string } | null {
  const code = row && row[0];
  const icon = EVENT_STATUS_ICON[code];
  return typeof icon === "string" ? { icon } : null;
}

// ── event_type: the autofilled-location field ───────────────────────────────
// Same "one derivation, three consumers" contract as activityCodeMeta above:
//   · server/scripts/seed_deploy_taxonomies.js — what the seed WRITES to meta
//   · useTaxonomy's toEntriesFromDefault      — the offline/outage fallback
//   · recordings/p20a-event-location          — the byte-identity guard
/** meta payload the seed writes for one DEFAULT_EVT_TYPES row. `color` was
 *  always there; `location` is the relocated autofill spec (P20a) and
 *  `firmWide` the relocated visibility carve-out (P20b) — each omitted
 *  entirely for a code that does not carry it, so the seed never writes a junk
 *  key and the merge never plants one on an admin-authored row. */
export function eventTypeMeta(x: any) {
  const meta: Record<string, any> = { color: x && x.color };
  if (x && x.location) meta.location = x.location;
  if (x && x.firmWide === true) meta.firmWide = true;
  if (x && x.m365ImportRole) meta.m365ImportRole = x.m365ImportRole;
  /* BRIEF 123 §1 — `isMeeting`, and it is stated as "the rows that carry a
     ROLE" rather than as "REU and VIR". That is the point of the whole brief:
     the app must stop knowing two Belgian type codes by heart. A default row
     the firm's M365 import routes meetings INTO is, at seed time, a meeting
     type; from the moment the row exists the two fields are independent and
     an admin edits either one on its own (SettingsTaxonomies META_FIELDS).

     A THIRD ROLE-CARRYING DEFAULT WOULD INHERIT THIS WITH NO EDIT, which a
     literal on two rows would not. And D28 means this reaches NEW rows and new
     tenants only — every REU/VIR row that already exists is a "present,
     differs → SKIP AND REPORT" for `_seed-row.js`, which is why §1 also ships
     server/scripts/seed_migration_123_event_type_is_meeting.js. */
  if (x && x.m365ImportRole) meta.isMeeting = true;
  /* BRIEF 124 §1 — `defaultMode`, and it is GATED ON `meta.isMeeting` here for
     the same reason the editor only offers it on a meeting row: a mode is not a
     question a non-meeting type has an answer to, and writing one would plant a
     preference nobody can see or edit. Unset is a first-class value — "no
     preference", which `meetingModeFor` reads as Physical. */
  if (meta.isMeeting === true && asMeetingMode(x && x.defaultMode)) {
    meta.defaultMode = x.defaultMode;
  }
  /* Brief 46 §1. Omitted ENTIRELY for a type that carries no schedule — an
     empty array is a configured "never", and absence is "nobody has decided",
     and the engine must be able to tell them apart. Writing `[]` here would
     plant a decision on every event type the seed touches. */
  const leads = normaliseAlertLeadDays(x && x.alertLeadDays);
  if (leads.length) meta.alertLeadDays = leads;
  return meta;
}

/** The days-before schedule, canonicalised: whole days, none negative, no
 *  duplicates, furthest-out first. Anything that is not a usable day is
 *  DROPPED rather than refusing the whole list — one mistyped entry must not
 *  cost a firm its sixty-day warning.
 *
 *  A cap, because this rides in a query bound: the engine widens its date
 *  window to the largest lead any type declares, so an admin who types 99999
 *  would otherwise make every tick scan three centuries of agenda.
 *
 *  WRITER SIDE. `server/src/lib/eventTypeAlertSchedule.js` is the reader, and
 *  is a second implementation on purpose — the server cannot import this file
 *  (the seed transpiles it; the engine cannot). That is the same split
 *  `m365ImportTypesFromEntries` here and `eventTypeImport.js` there already
 *  live with. What binds the two is not discipline: it is
 *  `probe-alert-engine.js`, which writes a schedule through this side and
 *  asserts the engine fires on exactly those days.  */
export const ALERT_LEAD_DAYS_MAX = 3650;
export function normaliseAlertLeadDays(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  const out = new Set<number>();
  for (const raw of v) {
    const n = typeof raw === "number" ? raw : Number(String(raw).trim());
    if (Number.isInteger(n) && n >= 0 && n <= ALERT_LEAD_DAYS_MAX) out.add(n);
  }
  return [...out].sort((a, b) => b - a);
}

/** 182 C.1 — `case_type.meta.silenceDays`, the writer side: a whole number of
 *  days in [1, MAX], or `undefined` (the key is removed — absence is not zero).
 *  The reader is `server/src/lib/eventTypeAlertSchedule.js` silenceDaysOf,
 *  a second implementation for the reason normaliseAlertLeadDays states; the
 *  two agree on the same bounds and the drive (scripts/drive-182-silence.js)
 *  writes through this side's rule and asserts the engine's. */
export const SILENCE_DAYS_MAX = 3650;
export function normaliseSilenceDays(v: unknown): number | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  let n: number;
  if (typeof v === "number") n = v;
  else if (typeof v === "string") n = Number(v.trim());
  else return undefined;
  return Number.isInteger(n) && n >= 1 && n <= SILENCE_DAYS_MAX ? n : undefined;
}

/** The two roles a Microsoft-365 meeting can land on. Exported so the seed,
 *  the server resolver and the recording all spell them the same way. */
export const M365_IMPORT_ROLES = ["virtual", "inPerson"];

/** Fold loaded event_type entries into { virtual, inPerson } — the code an
 *  imported online / physical meeting becomes. A role no type claims is
 *  absent; if several claim it the FIRST in taxonomy order wins, so a
 *  mis-tagged extra row cannot silently change what the earlier one meant. */
export function m365ImportTypesFromEntries(entries: any[] | null | undefined) {
  const out: Record<string, string> = {};
  for (const e of entries || []) {
    const role = e && e.meta && e.meta.m365ImportRole;
    if (role && M365_IMPORT_ROLES.includes(role) && out[role] === undefined) {
      out[role] = e.code;
    }
  }
  return out;
}

/** BRIEF 123 §1/§3 — the event_type codes a firm has flagged as MEETING types
 *  (`meta.isMeeting`). Its sibling above answers "which type does an imported
 *  M365 meeting BECOME" and can name at most one code per role; this answers
 *  "which of this firm's types ARE meetings" and any number may be. A firm that
 *  holds hearings by videoconference ticks AUD and gets an end time, a
 *  Physical/Online choice and an invitation list on a hearing.
 *
 *  A SET, and the codes are the firm's own — never `REU`/`VIR` written down.
 *  That literal pair is what `EV_MEETING_KIND` was, and it broke on the first
 *  firm that renamed one (tenant 4 on this box has). */
export type MeetingTypeEntry = {
  code?: string | null;
  archived?: boolean | null;
  meta?: { isMeeting?: unknown; defaultMode?: unknown } | null;
};
export function meetingTypeCodes(
  entries: readonly MeetingTypeEntry[] | null | undefined,
) {
  const out = new Set<string>();
  for (const e of entries || []) {
    if (e && e.code && e.meta && e.meta.isMeeting === true) out.add(String(e.code));
  }
  return out;
}

/** BRIEF 124 §1 — the mode each meeting type PREFERS, folded once, exactly as
 *  `meetingTypeCodes` above folds which types are meetings at all. `{ REU:
 *  "person", VIR: "online" }` on the seeded taxonomy — and on the firm's own
 *  codes, never on those two written down.
 *
 *  A type absent from this map has declared no preference, which is not the
 *  same as declaring Physical: `meetingModeFor` supplies Physical as the
 *  floor, and it does so in ONE place so the two absences cannot drift apart.
 *
 *  ARCHIVED ROWS ARE INCLUDED BY THE CALLER, like both folds above and for
 *  #191's reason: an archived meeting type still has events pointing at it, and
 *  opening one of those must not silently change what mode it prefers. */
export function meetingDefaultModes(
  entries: readonly MeetingTypeEntry[] | null | undefined,
): Record<string, MeetingMode> {
  const out: Record<string, MeetingMode> = {};
  for (const e of entries || []) {
    if (!e || !e.code || !e.meta || e.meta.isMeeting !== true) continue;
    const mode = asMeetingMode(e.meta.defaultMode);
    if (mode) out[String(e.code)] = mode;
  }
  return out;
}

/** BRIEF 139 §1 — THE LONGEST CODE THE TAXONOMY CAN PUT ON SCREEN. Folded
 *  once, beside its siblings and for their reason: a fold over taxonomy entries
 *  belongs where the folds are, and no ROW re-derives it.
 *
 *  It answers one question — which code does `TypeTagBox` render invisibly, so
 *  the box sizes itself to the widest chip the tenant's own taxonomy can show —
 *  and the caller hands it THE LIST THE CHIP RESOLVES AGAINST, so the mirror and
 *  what it mirrors cannot name different populations. For events that is
 *  `evtTypes` (App.tsx), the NON-archived display list: an archived type can no
 *  longer be put on an event, and 139 §1's ruling is that archiving a long code
 *  narrows the column again. That is the opposite of the three folds above, and
 *  deliberately so — they answer questions about an event THAT ALREADY EXISTS
 *  (#191), this one answers what a column must RESERVE.
 *
 *  A DIVIDER IS SKIPPED, exactly as `eventTypeChips` skips it (108 §6b): the
 *  reserved `__div__…` prefix is longer than most real codes and no event can
 *  ever carry it, so one divider would otherwise reserve every event surface a
 *  column for a code that cannot appear.
 *
 *  LONGEST BY CHARACTER COUNT, and that is a CHOICE, not a measurement. The
 *  WIDTH is measured by the browser, because what this returns is RENDERED as
 *  the mirror rather than converted into pixels — 139 §1.2 forbids `ch` units
 *  and text measurement, and `etTag` declares no `fontFamily` to do the
 *  arithmetic against anyway. At a proportional font a shorter code can be
 *  wider; when it is, the box falls a few pixels short and the chip's own
 *  ellipsis takes it (App.tsx), which is the same cap a code wider than
 *  `TYPE_COL_MAX_W` gets. */
export function longestTaxonomyCode(
  entries: readonly { code?: string | null }[] | null | undefined,
): string {
  let out = "";
  for (const e of entries || []) {
    if (!e || !e.code || isDividerCode(e.code)) continue;
    if (e.code.length > out.length) out = e.code;
  }
  return out;
}

/** BRIEF 123 §3 — the type a "schedule a meeting" BUTTON should open on: the
 *  first meeting type in taxonomy order, exactly the rule
 *  `m365ImportTypesFromEntries` uses to resolve a role. The parties-pane and
 *  internal-meeting entry points seeded the literal `"REU"`; they ask this now,
 *  so a firm that renames or reorders its meeting types gets its own answer.
 *
 *  `null` when the firm has flagged NONE, and the caller then seeds no type at
 *  all rather than a code the taxonomy may not contain — an empty type field the
 *  user fills is honest; a dangling code is not.
 *
 *  `entries` must arrive in taxonomy order, which is what `useTaxonomy` returns
 *  and what the seed writes (`order` asc, then id). */
export function firstMeetingTypeCode(
  entries: readonly MeetingTypeEntry[] | null | undefined,
): string | null {
  for (const e of entries || []) {
    if (e && e.code && e.meta && e.meta.isMeeting === true && !e.archived) return String(e.code);
  }
  return null;
}

/** Fold loaded event_type entries into the { code: locationSpec } lookup the
 *  agenda modal's autofill reads. Codes with no meta.location are absent —
 *  resolveEventLocation() answers "" for them. */
export function eventLocationsFromEntries(entries: any[] | null | undefined) {
  const map: Record<string, any> = {};
  for (const e of entries || []) {
    const spec = e && e.meta && e.meta.location;
    if (spec && spec.mode) map[e.code] = spec;
  }
  return map;
}

/** Safe-access helper: returns entry.meta or {}. */
export function extractMeta(entry: any) {
  return entry && entry.meta && typeof entry.meta === "object"
    ? entry.meta
    : {};
}

// Phase 7 Stage 2 — long-label channel. Some taxonomies (initially
// item_level) carry a longer multilingual label inside meta for use
// in dropdowns where the short pill-style label is too terse. Returns
// the active-language long label if present, else cascades:
//   meta.longLabel{LangXX} → meta.longLabelEn → pickEntryLabel(entry)
// Same lang-detection as pickEntryLabel (window.__lxLang) so this works
// from non-React call sites.
export function resolveLongLabel(entry: any) {
  if (!entry) return "";
  const meta = entry.meta && typeof entry.meta === "object" ? entry.meta : {};
  const lang = (typeof window !== "undefined" && window.__lxLang) || "en";
  if (lang === "fr" && meta.longLabelFr) return meta.longLabelFr;
  if (lang === "nl" && meta.longLabelNl) return meta.longLabelNl;
  if (meta.longLabelEn) return meta.longLabelEn;
  return pickEntryLabel(entry);
}
